import { useEffect, useState } from "react";
import { t } from "i18next";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";

import { useLazyGetCommentsQuery } from "@/redux/service";
import BasePagination from "@/components_v2/Pagination/BasePagination";
import { PaginatedResponse } from "@/redux/types";
import {
  CommentFiltersState,
  CommentListProps,
  CommentsDataState,
} from "@/domains/community/types";
import {
  CommentQueryParams,
  FullCommentResponse,
} from "@/redux/comments/types";
import CommentFilters from "@/domains/community/components/CommentFilters";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import SearchXIcon from "@/assets/icons/search-x.svg?react";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import HeartIcon from "@/assets/icons/heart.svg?react";
import DiscussionIcon from "@/assets/icons/discussions-icon.svg?react";
import { DiscussionQueryParams } from "@/redux/discussions/types";
import TextElement from "@/components_v2/ContentElements/TextElement";
import { TextType } from "@/components_v2/ContentElements/types";
import { DEFAULT_PAGE_SIZE } from "@/shared/utils/constants";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";

const CommentList = ({ defaultFilters }: CommentListProps) => {
  const [getComments, { isLoading }] = useLazyGetCommentsQuery();
  const [filters, setFilters] = useState({});
  const [commentsData, setCommentsData] = useState<CommentsDataState>({
    page: 1,
    total: 0,
    data: [],
  });

  const transformFiltersForAPI = (
    filters: CommentFiltersState,
  ): CommentQueryParams => {
    const { createdRange, ...rest } = filters;

    const transformed: DiscussionQueryParams = {
      ...rest,
    };

    if (
      createdRange &&
      Array.isArray(createdRange) &&
      createdRange.length === 2
    ) {
      const [from, to] = createdRange;
      if (from) transformed.startDate = from.toFormat("yyyy-MM-dd");
      if (to) transformed.endDate = to.toFormat("yyyy-MM-dd");
    }

    return transformed;
  };

  const fetchComments = async (
    page = 1,
    currentFilters: CommentFiltersState = filters,
  ) => {
    const processedFilters = transformFiltersForAPI(currentFilters);
    const response = (await getComments({
      page: page,
      pageSize: DEFAULT_PAGE_SIZE,
      ...defaultFilters,
      ...processedFilters,
    }).unwrap()) as PaginatedResponse<FullCommentResponse>;

    setCommentsData({
      page: page,
      total: response.count,
      data: response.results,
    });
  };

  useEffect(() => {
    fetchComments(1);
  }, []);

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      fetchComments(1, filters);
    }
  }, [filters]);

  const handlePagination = (newPage: number) => {
    if (newPage !== commentsData.page && newPage > 0) {
      fetchComments(newPage);
    }
  };

  return (
    <div className="table-container-layout">
      <CommentFilters
        setFilters={setFilters}
        filterNames={["search", "createdRange"]}
        defaultFilters={defaultFilters}
      />

      {isLoading && <BaseSpinner />}

      <div className="flex flex-col gap-400">
        {commentsData.data.map((comment) => (
          <div
            key={comment.id}
            className="comment-item flex flex-col gap-250 items-start"
          >
            <p className="body-m-regular">
              <TextElement
                value={comment.content}
                type={TextType.RICH_TEXT}
                lineClampNumber={3}
              />
            </p>

            <BaseDivider />

            <div className="comment-item-footer body-s-regular flex flex-wrap justify-between items-center self-stretch gap-200">
              <div className="flex flex-wrap gap-100 items-center">
                {t("community.labels.postedOnDateIn", {
                  date: DateTime.fromISO(comment.createdAt).toFormat(
                    "dd.MM.yyyy",
                  ),
                })}

                <Link to={`/discussions/${comment.discussion}`}>
                  {comment.discussionTitle || ""}
                </Link>
              </div>

              <div className="flex flex-wrap gap-250 items-center">
                <div className="flex items-center gap-100">
                  <BaseSvgRenderer component={HeartIcon} />
                  {t("community.labels.likes", { count: comment.likesCount })}
                </div>
                <div className="flex items-center gap-100">
                  <BaseSvgRenderer component={DiscussionIcon} />
                  {t("community.labels.replies", {
                    count: comment.repliesCount,
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {commentsData.data.length > 0 && (
        <BasePagination
          current={commentsData.page}
          total={commentsData.total}
          pageSize={10}
          onChange={handlePagination}
        />
      )}

      {commentsData.data.length === 0 && !isLoading && (
        <div className="empty-state-container full-width flex flex-col items-center justify-center">
          <BaseSvgRenderer component={SearchXIcon} />
          <h6 className="header-m-medium">
            {t("community.labels.noCommentsFound")}
          </h6>
        </div>
      )}
    </div>
  );
};

export default CommentList;
